import { useEffect, useState } from 'react'
import {
  useLazyFetchMapSchemasQuery,
  useLazyGetSchemaDetailQuery,
} from 'features/private-views/map-schema/mapSchemaApiSlice'
import { normalizeRule, pickSchema } from 'features/private-views/map-schema/schemaRuleUtils'

const EMPTY = { rule: null, unitByItemId: {} }

// Loads the schema behind a mapped item: its AND/OR/XOR rule and the unit each input is expected in.
// Falls back to EMPTY when nothing matches or loading fails; the backend still enforces the rule on save.
export const useSchemaRule = (itemId, recipeItemIds = []) => {
  const [fetchSchemas] = useLazyFetchMapSchemasQuery()
  const [getSchemaDetail] = useLazyGetSchemaDetailQuery()
  const [schema, setSchema] = useState(EMPTY)
  const recipeKey = recipeItemIds.join(',')

  useEffect(() => {
    setSchema(EMPTY)
    if (!itemId) return undefined
    let cancelled = false
    const load = async () => {
      try {
        const list = await fetchSchemas({ itemId, size: 50 }, true).unwrap()
        const details = await Promise.all(
          (list?.items || []).map((s) => getSchemaDetail(s.id, true).unwrap().catch(() => null)),
        )
        const best = pickSchema(details, recipeKey ? recipeKey.split(',') : [])
        const unitByItemId = {}
        ;(best?.input || []).forEach((inp) => {
          // expectedUnit is the unit set in the schema; `unit` on the same row is the item's master unit
          if (inp.expectedUnit) unitByItemId[Number(inp.itemId)] = inp.expectedUnit
        })
        if (!cancelled) setSchema({ rule: normalizeRule(best?.rule), unitByItemId })
      } catch {
        if (!cancelled) setSchema(EMPTY)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [itemId, recipeKey, fetchSchemas, getSchemaDetail])

  return schema
}

// Recipes in the schema's unit, so auto populate, the batch filter and labels all use it
export const withSchemaUnits = (recipes, unitByItemId) =>
  recipes.map((r) => (unitByItemId[Number(r.id)] ? { ...r, unit: unitByItemId[Number(r.id)] } : r))
